import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { withArabicDigits, formatDateYYYYMMDD } from '../../../utils/numbers';


const ArticleCard = ({ article, isDiscussion = false, isBookmarked = false, onBookmarkToggle, isAuthenticated = false }) => {
  const navigate = useNavigate();

  const getCircleColor = (circle) => {
    // Normalize category name (remove 'دائرة' if present) to match keys
    const normalized = circle?.replace('دائرة ', '') || '';

    const colors = {
      'النفس': '#7D8471',
      'الأدب': '#8B6914',
      'الشعر': '#B8860B',
      'العقل': '#6B5B47',
      'العين': '#D4AF37',
      'الصوت': '#8B7355',
      'التراث': '#8B4513',
      'التصوف': '#5A5A5A',
      'الفنون': '#A0826D',
      'اللسان': '#6B8E23',
      'دائرة الرحلة': '#9B7653',
      'دائرة الدائرة': '#5A5A5A'
    };
    return colors?.[normalized] || '#C5A059';
  };

  const handleArticleClick = () => {
    if (isDiscussion || article?.type === 'Discussion') {
      navigate(`/discussion/${article?.id}`);
    } else {
      navigate(`/article/${article?.id}`);
    }
  };

  const handleBookmarkClick = (e) => {
    e?.stopPropagation();
    if (onBookmarkToggle && isAuthenticated) {
      onBookmarkToggle(article?.id);
    }
  };

  return (
    <article
      onClick={handleArticleClick}
      className="group bg-[var(--color-background)] overflow-hidden transition-all duration-300 flex flex-col h-full cursor-pointer">

      <div className="w-full flex items-center justify-center p-4 lg:p-6 lg:mr-3">
        <div className="relative w-full max-w-[200px] lg:max-w-[13rem]">
          <div className="relative w-full aspect-square rounded-full overflow-hidden levitation-shadow">
            <Image
              src={article?.image}
              alt={article?.imageAlt}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />

            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent mr-2.5" />
          </div>

          {/* Article Type + Bookmark — top left of image */}
          <div className="absolute top-1 left-0 z-10 flex flex-col items-start gap-2">
            <div
              className="px-3 py-1.5 rounded-full backdrop-blur-sm shadow-warm"
              style={{ backgroundColor: `${getCircleColor(article?.circle)}4D` }}
            >
              <span
                className="text-[10px] lg:text-xs font-bold text-white"
                style={{ fontFamily: 'Amiri, serif' }}
              >
                {article?.type === 'Discussion' ? 'حلقة نقاش' : 'مقال'}
              </span>
            </div>

            <button
              onClick={handleBookmarkClick}
              className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-2 rounded-full backdrop-blur-sm bg-white/90 hover:bg-white shadow-warm flex items-center justify-center"
              aria-label="Bookmark article">
              <Icon
                name="Bookmark"
                size={16}
                color={isBookmarked ? getCircleColor(article?.circle) : 'var(--color-muted-foreground)'}
                fill={isBookmarked ? getCircleColor(article?.circle) : 'none'} />
            </button>
          </div>

          {/* Circle title — top right of image */}
          <div className="absolute top-1 right-0 z-10 flex flex-col items-end gap-2">
            <div
              className="flex items-center gap-2 px-3 lg:px-4 py-1.5 lg:py-2 rounded-full backdrop-blur-sm shadow-warm"
              style={{ backgroundColor: `${getCircleColor(article?.circle)}4D` }}>

              <Icon name="Circle" size={6} color="white" fill="white" />
              <span
                className="text-xs lg:text-sm font-bold text-white whitespace-nowrap"
                style={{ fontFamily: 'Amiri, serif', fontWeight: 700 }}>

                {article?.circle}
              </span>
            </div>
          </div>

          {/* Stats — bottom left of image */}
          <div className="absolute bottom-1 left-0 flex items-center gap-2 z-10">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm shadow-warm">
              <Icon name="Clock" size={14} color="var(--color-foreground)" />
              <span className="text-xs font-medium text-[var(--color-foreground)]" style={{ fontFamily: 'Amiri, serif', fontWeight: 400 }}>
                {withArabicDigits(article?.readTime ?? '')}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col flex-1 p-4 lg:p-6 text-center">
        <h3 className="text-[22px] font-bold text-[var(--color-foreground)] mt-4 lg:mt-6 mb-3 line-clamp-2 group-hover:text-[var(--color-primary)] transition-colors" style={{ fontFamily: 'Amiri, serif', fontWeight: 700 }}>
          {article?.title}
        </h3>

        <p className="text-[16px] text-[#5A5A5A] mb-5 line-clamp-3 flex-1" style={{ fontFamily: 'Amiri, serif', fontWeight: 400, lineHeight: '1.8' }}>
          {article?.excerpt}
        </p>

        <div
          className="flex items-center justify-center gap-3 pt-4 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            if (article?.authorId) navigate(`/user/${article.authorId}`);
          }}
        >
          <Image
            src={article?.authorImage}
            alt={article?.authorImageAlt}
            className="w-10 h-10 rounded-full object-cover levitation-shadow"
            type="user" />

          <div className="flex flex-col items-start">
            <span className="text-sm font-medium text-[var(--color-foreground)] hover:text-[var(--color-primary)] transition-colors" style={{ fontFamily: 'Amiri, serif', fontWeight: 700 }}>
              {article?.author}
            </span>
            <span className="text-xs" style={{ fontFamily: 'Amiri, serif', fontWeight: 400, color: '#999' }} dir="ltr">
              {formatDateYYYYMMDD(article?.publishedDate ?? '')}
            </span>
          </div>
        </div>
      </div>
    </article>);

};

export default ArticleCard;